import React, { useState, useMemo } from 'react';
import { API_BASE } from '../../constants/urls';

const ArticleOverviewTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'processed_date',
    direction: 'descending'
  });
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [journalFilter, setJournalFilter] = useState('all');
  const [teFilter, setTeFilter] = useState('all');
  const [ceFilter, setCeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Get unique values for filter options
  const filterOptions = useMemo(() => {
    if (!data) return { journals: [], tes: [], ces: [], statuses: [] };
    
    const journals = [...new Set(data.map(item => item.journal_code).filter(Boolean))];
    const tes = [...new Set(data.map(item => item.te_name).filter(Boolean))];
    const ces = [...new Set(data.map(item => item.ce_name).filter(Boolean))];
    const statuses = [...new Set(data.map(item => item.status).filter(Boolean))];
    
    return { journals, tes, ces, statuses };
  }, [data]);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    if (!data) return [];
    
    // Apply filters
    let filteredData = data.filter(item => {
      // Search filter
      const searchMatch = !searchTerm || 
        item.article_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.journal_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.journal_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.te_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ce_name.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Status filter
      const statusMatch = statusFilter === 'all' || item.status === statusFilter;
      
      // Journal filter
      const journalMatch = journalFilter === 'all' || item.journal_code === journalFilter;
      
      // TE filter
      const teMatch = teFilter === 'all' || item.te_name === teFilter;
      
      // CE filter
      const ceMatch = ceFilter === 'all' || item.ce_name === ceFilter;
      
      return searchMatch && statusMatch && journalMatch && teMatch && ceMatch;
    });
    
    // Apply sorting
    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filteredData;
  }, [data, sortConfig, searchTerm, statusFilter, journalFilter, teFilter, ceFilter]);

  // Get sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'ascending' ? ' Asc' : ' Desc';
  };

  // Status badge styling
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed': return 'completed';
      case 'assigned_CE': return 'ce-assigned';
      case 'assigned_TE': return 'te-assigned';
      case 'TE Submitted': return 'te-submitted';
      case 'CE Submitted': return 'ce-submitted';
      case 'processed': return 'processed';
      default: return 'default';
    }
  };

  // Format file link for display
  const formatFileLink = (link) => {
    if (!link || link === 'N/A' || link === '') return 'N/A';
    if (link.startsWith('http')) {
      return (
        <a href={link} target="_blank" rel="noopener noreferrer" className="file-link">
          View File
        </a>
      );
    }
    // If it's an S3 key, create view link
    const viewLink = `${API_BASE}/api/storage/view/${link}`;
    return (
      <a href={viewLink} target="_blank" rel="noopener noreferrer" className="file-link">
        View File
      </a>
    );
  };

  // Format TAT display
  const formatTAT = (tatDays) => {
    if (tatDays === null || tatDays === undefined) return 'N/A';
    if (tatDays < 0) return 'Invalid';
    return `${tatDays} days`;
  };

  if (!data || data.length === 0) {
    return (
      <div className="stats-table-container">
        <div className="no-data">
          <p>No article data available for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-table-container">
      <div className="table-controls">
        <div className="search-section">
          <input
            type="text"
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button 
            className="filter-toggle-button"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
        
        {showFilters && (
          <div className="filters-section">
            <div className="filter-group">
              <label>Status:</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Statuses</option>
                {filterOptions.statuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>Journal:</label>
              <select 
                value={journalFilter} 
                onChange={(e) => setJournalFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Journals</option>
                {filterOptions.journals.map(journal => (
                  <option key={journal} value={journal}>{journal}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>TE:</label>
              <select 
                value={teFilter} 
                onChange={(e) => setTeFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All TEs</option>
                {filterOptions.tes.map(te => (
                  <option key={te} value={te}>{te}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>CE:</label>
              <select 
                value={ceFilter} 
                onChange={(e) => setCeFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All CEs</option>
                {filterOptions.ces.map(ce => (
                  <option key={ce} value={ce}>{ce}</option>
                ))}
              </select>
            </div>
            
            <button 
              className="clear-filters-button"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setJournalFilter('all');
                setTeFilter('all');
                setCeFilter('all');
              }}
            >
              Clear All
            </button>
          </div>
        )}
      </div>
      
      <div className="table-info">
        <p>Showing {filteredAndSortedData.length} of {data.length} articles</p>
      </div>
      
      <div className="table-wrapper">
        <table className="stats-table article-overview-table">
          <thead>
            <tr>
              <th 
                className="sortable"
                onClick={() => handleSort('article_id')}
              >
                Article ID{getSortIndicator('article_id')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('journal_code')}
              >
                Journal{getSortIndicator('journal_code')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('status')}
              >
                Status{getSortIndicator('status')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('processed_date')}
              >
                Processed{getSortIndicator('processed_date')}
              </th>
              <th className="non-sortable">
                File Link
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('te_upload_date')}
              >
                TE Date{getSortIndicator('te_upload_date')}
              </th>
              <th className="non-sortable">
                TE Link
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('te_word_count')}
              >
                TE WC{getSortIndicator('te_word_count')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('ce_update_date')}
              >
                CE Date{getSortIndicator('ce_update_date')}
              </th>
              <th className="non-sortable">
                CE Link
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('ce_word_count')}
              >
                CE WC{getSortIndicator('ce_word_count')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('completion_date')}
              >
                Completed{getSortIndicator('completion_date')}
              </th>
              <th 
                className="sortable number"
                onClick={() => handleSort('tat_days')}
              >
                TAT{getSortIndicator('tat_days')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('te_name')}
              >
                TE{getSortIndicator('te_name')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('ce_name')}
              >
                CE{getSortIndicator('ce_name')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.map((article, index) => (
              <tr key={article.article_id} className="stats-row">
                <td className="article-id-cell">
                  <div className="article-id">{article.article_id}</div>
                </td>
                <td className="journal-cell">
                  <div className="journal-info">
                    <div className="journal-code">{article.journal_code}</div>
                    <div className="journal-name">{article.journal_name}</div>
                  </div>
                </td>
                <td className="status-cell">
                  <span className={`status-badge ${getStatusBadgeClass(article.status)}`}>
                    {article.status}
                  </span>
                </td>
                <td className="date-cell">
                  <span className="date-value">{article.processed_date || 'N/A'}</span>
                </td>
                <td className="link-cell">
                  {formatFileLink(article.file_link)}
                </td>
                <td className="date-cell">
                  <span className="date-value">{article.te_upload_date || 'N/A'}</span>
                </td>
                <td className="link-cell">
                  {formatFileLink(article.te_upload_link)}
                </td>
                <td className="number-cell">
                  <span className="word-count">
                    {article.te_word_count ? article.te_word_count.toLocaleString() : 'N/A'}
                  </span>
                </td>
                <td className="date-cell">
                  <span className="date-value">{article.ce_update_date || 'N/A'}</span>
                </td>
                <td className="link-cell">
                  {formatFileLink(article.ce_update_link)}
                </td>
                <td className="number-cell">
                  <span className="word-count">
                    {article.ce_word_count ? article.ce_word_count.toLocaleString() : 'N/A'}
                  </span>
                </td>
                <td className="date-cell">
                  <span className="date-value completion-date">
                    {article.completion_date || 'N/A'}
                  </span>
                </td>
                <td className="number-cell">
                  <span className="tat-days">
                    {formatTAT(article.tat_days)}
                  </span>
                </td>
                <td className="user-cell">
                  <span className="user-name">{article.te_name}</span>
                </td>
                <td className="user-cell">
                  <span className="user-name">{article.ce_name}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-legend">
        <h4>Status Legend:</h4>
        <div className="legend-items">
          <div className="legend-item completed">
            <span className="indicator"></span>
            <span>Completed</span>
          </div>
          <div className="legend-item ce-assigned">
            <span className="indicator"></span>
            <span>Assigned to CE</span>
          </div>
          <div className="legend-item te-assigned">
            <span className="indicator"></span>
            <span>Assigned to TE</span>
          </div>
          <div className="legend-item te-submitted">
            <span className="indicator"></span>
            <span>TE Submitted</span>
          </div>
          <div className="legend-item ce-submitted">
            <span className="indicator"></span>
            <span>CE Submitted</span>
          </div>
          <div className="legend-item processed">
            <span className="indicator"></span>
            <span>Processed</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleOverviewTable;
